'use client';

import IntrobannerModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/IntrobannerModal';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { isDesktop } from 'react-device-detect';
import {
  BottomPopup,
  FullPopupButton,
} from '@wconcept/wconcept-common/models/renewal/popup';
import useHistoryRouter from '@hooks/useLinkTo';
import useGlobalState from '@hooks/useGlobalState';
import dayjs from 'dayjs';
import {
  getCookieWckBottomPopup,
  getCookieWckFullPagePopup,
  setCookieWckBottomPopup,
  setCookieWckFullPagePopup,
} from '@wconcept/wconcept-common/utils/cookie';
import useGa4Event from '@hooks/useGa4Event';
import { useMarketingPopupState } from '@contexts/MarketingPopupStateContext';
import MainSlideModal2 from '@wconcept/wconcept-ui-renewal/components/organisms/_modal/MainSlideModal2';
import { useWomenPageController } from '../WomenPageControllerProvider';

function WomenPagePopupManager() {
  const { openModal, closeModal } = useContext(ModalsContext);
  const {
    setFullPopupIsClosed,
    getFullPopupIsClosed,
    setCoachMarkIsClosed,
    setBottomPopupClosed,
  } = useWomenPageController();
  const { linkTo } = useHistoryRouter();
  const { isMobile } = useGlobalState();
  const { sendGa4ButtonClick, sendGa4BannerClick } = useGa4Event();

  const { marketingPopup } = useMarketingPopupState();
  const popupShownRef = useRef(false);

  const setDontShowTodayPopup = useCallback((type: string) => {
    const nowDate = dayjs().format('YYYY-MM-DD');
    if (type === 'fullPopup') {
      setCookieWckFullPagePopup(nowDate);
    } else if (type === 'bottomPopup') {
      setCookieWckBottomPopup(nowDate);
    }
  }, []);

  const checkBottomPopup = useCallback(
    () =>
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async resolve => {
        const nowDate = dayjs();
        const wckBottomPopup = await getCookieWckBottomPopup();
        const useBottomPopup = !nowDate.isSame(wckBottomPopup, 'day');

        const hasPopupData =
          useBottomPopup &&
          marketingPopup?.bottomPopupList &&
          marketingPopup?.bottomPopupList.length > 0;

        if (hasPopupData) {
          setBottomPopupClosed(false);
          openModal(MainSlideModal2, {
            type: isDesktop ? 'nonModal' : 'drawer',
            onClose: () => {
              setBottomPopupClosed(true);
              resolve(true);
              popupShownRef.current = false;
              sendGa4ButtonClick({
                text: '닫기',
                area: 'home_popup_close_button',
              });
            },
            onSubmit: () => {
              setBottomPopupClosed(true);
              resolve(true);
              popupShownRef.current = false;
              sendGa4ButtonClick({
                text: '오늘 하루 보지않기',
                area: 'home_popup_close_button',
              });
              setDontShowTodayPopup('bottomPopup');
            },
            value: {
              items: marketingPopup?.bottomPopupList,
              onClickButton: (v: BottomPopup, i: number) => {
                sendGa4BannerClick({
                  listNo: i + 1,
                  area: 'home_popup_banner',
                  bannerType: v?.ga4ClickEvent?.eventType || '',
                  issueCd: v?.ga4ClickEvent?.eventValue || '',
                  text: `${v?.bottomPopupTitle1} ${v?.bottomPopupTitle2}`,
                  issueNm: `${v?.bottomPopupTitle1} ${v?.bottomPopupTitle2}`,
                });
                if (v.landingUrl) {
                  linkTo.push({
                    url: v.landingUrl,
                  });
                }
              },
              isDesktop: !isMobile,
            },
          });
          popupShownRef.current = true;
        } else {
          resolve(true);
        }
      }),
    [
      marketingPopup,
      isMobile,
      linkTo,
      openModal,
      sendGa4BannerClick,
      sendGa4ButtonClick,
      setDontShowTodayPopup,
    ]
  );

  const checkFullPopup = useCallback(
    () =>
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async resolve => {
        const nowDate = dayjs();
        const wckfFullPagePopup = await getCookieWckFullPagePopup();
        const useFullpagePopup = !nowDate.isSame(wckfFullPagePopup, 'day');

        if (
          useFullpagePopup &&
          marketingPopup?.fullPopup &&
          !getFullPopupIsClosed()
        ) {
          openModal(IntrobannerModal, {
            type: 'modal',
            onClose: () => {
              resolve(true);
              setFullPopupIsClosed(true);
              sendGa4ButtonClick({
                text: '닫기',
                area: 'home_fullpopup_close_button',
              });
            },
            onSubmit: () => {
              resolve(true);
              popupShownRef.current = false;
              setDontShowTodayPopup('fullPopup');
              sendGa4ButtonClick({
                text: '오늘 하루 보지않기',
                area: 'home_fullpopup_close_button',
              });
            },
            value: {
              fullPopupButtons: marketingPopup?.fullPopup?.fullPopupButtons,
              imgSrc: marketingPopup?.fullPopup?.imageUrl,
              onClickButton: (v: FullPopupButton, i: number) => {
                closeModal(MainSlideModal2);
                resolve(true);

                setFullPopupIsClosed(true);
                sendGa4ButtonClick({
                  text: v.buttonTitle,
                  area: 'home_fullpopup_button',
                  bannerType: v?.ga4ClickEvent?.eventType || '',
                  issueCd: v?.ga4ClickEvent?.eventValue || '',
                  listNo: i + 1,
                });
                closeModal(IntrobannerModal);
                linkTo.push({
                  url: v.landingUrl,
                });
              },
            },
          });
        } else {
          resolve(true);
        }
      }),
    [
      marketingPopup?.fullPopup,
      getFullPopupIsClosed,
      openModal,
      setFullPopupIsClosed,
      sendGa4ButtonClick,
      setDontShowTodayPopup,
      closeModal,
      linkTo,
    ]
  );

  useEffect(() => {
    if (marketingPopup && !popupShownRef.current) {
      if (isMobile) {
        checkFullPopup().then(() => {
          setTimeout(() => {
            // 애니메이션 이슈로 인해 setTimeout 추가
            checkBottomPopup();
          }, 300);
        });
      } else {
        checkFullPopup().then(() => {
          checkBottomPopup();
        });
      }
    }
  }, [marketingPopup]);

  const checkBottomPopupRef = useRef(checkBottomPopup);
  const checkFullPopupRef = useRef(checkFullPopup);

  useEffect(() => {
    checkBottomPopupRef.current = checkBottomPopup;
    checkFullPopupRef.current = checkFullPopup;
  }, [checkBottomPopup, checkFullPopup]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        if (isMobile) {
          if (!popupShownRef.current) {
            checkFullPopupRef.current().then(() => {
              checkBottomPopupRef.current();
            });
          }
        } else {
          popupShownRef.current = false;
          checkFullPopupRef.current();
          checkBottomPopupRef.current();
        }
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [isMobile]);

  return null;
}

export default WomenPagePopupManager;
